import { useState, useEffect, useRef } from "react";
import { Plus, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { STABLE_INPUT_CLASS } from "@/lib/utils-ros";
import { AIInsightPanel } from "@/components/revenueos/AIInsightPanel";

/** Auto-growing textarea — B-15: long coach actions stay fully readable. */
function ActionField({ value, onChange, onBlur, placeholder, testId, navy }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, 36)}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      data-testid={testId}
      className={`flex-1 ${STABLE_INPUT_CLASS} ${navy ? "focus:border-[#07539A]" : ""} text-[13px] py-1.5 placeholder:text-[var(--text-dim)] resize-none overflow-hidden leading-relaxed min-h-[2.25rem]`}
    />
  );
}

/**
 * ActionEditor — editable action items with in-place AI insight (B-13/B-15).
 */
export function ActionEditor({
  actions = [],
  aiInsights,
  onSave,
  onRegenerate,
  single = false,
  title = "Actions to Take",
  aiTitle = "AI Insights",
  insightAnchorId,
  insightLoading = false,
  insightMarkdown = false,
  showInsight = true,
  accent = "copper",
  rounded = false,
  insightFirst = false,
  insightActionLabel,
  readOnly = false,
}) {
  const navy = accent === "navy";
  const [items, setItems] = useState(() => (single ? [actions || ""] : (actions.length ? actions : [""])));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [insight, setInsight] = useState(aiInsights);
  const [insightError, setInsightError] = useState(null);

  useEffect(() => {
    if (dirty) return;
    setItems(single ? [actions || ""] : (actions.length ? actions : [""]));
  }, [actions, single, dirty]);

  useEffect(() => {
    if (!regenerating && !insightLoading) setInsight(aiInsights);
  }, [aiInsights, regenerating, insightLoading]);

  function updateItem(i, v) {
    const next = [...items]; next[i] = v; setItems(next); setDirty(true);
  }
  function addItem() { setItems([...items, ""]); setDirty(true); }
  function removeItem(i) {
    const next = items.filter((_, idx) => idx !== i);
    setItems(next.length ? next : [""]);
    setDirty(true);
  }

  async function save() {
    if (!dirty || readOnly) return;
    setSaving(true);
    try {
      const cleaned = items.map(x => x.trim()).filter(Boolean);
      const result = await onSave(single ? (cleaned[0] || "") : cleaned);
      setDirty(false);
      setSavedAt(Date.now());
      const n = result?.tasks_created?.length || 0;
      if (n > 0) toast.success(`${n} task${n > 1 ? "s" : ""} added to My Work`, { icon: <Check size={14} /> });
      setTimeout(() => setSavedAt(null), 2000);
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  async function regenerate() {
    if (!onRegenerate) return;
    setRegenerating(true);
    setInsightError(null);
    try {
      const result = await onRegenerate();
      if (result?.ai_summary) setInsight(result.ai_summary);
      if (result?.ai_next_actions?.length) {
        setItems(single ? [result.ai_next_actions[0] || ""] : result.ai_next_actions);
        setDirty(false);
      }
    } catch {
      setInsightError("Could not generate insight — try again.");
      toast.error("Refresh failed");
    } finally { setRegenerating(false); }
  }

  return (
    <div className="space-y-3" data-testid="action-editor-block">
      {showInsight && insightFirst && (
        <AIInsightPanel
          title={aiTitle}
          insight={insight}
          loading={regenerating || insightLoading}
          error={insightError}
          onRegenerate={!readOnly && onRegenerate ? regenerate : undefined}
          emptyText="No AI insight yet. Use refresh to generate one for this record."
          anchorId={insightAnchorId}
          alwaysShow
          markdown={insightMarkdown}
          accent={navy ? "navy" : "copper"}
          regenerateLabel={insightActionLabel}
        />
      )}
      <div className={`border p-3 md:p-4 ${rounded ? "rounded-[20px] border-[#C9DCE6] bg-white shadow-[0_7px_20px_rgba(7,83,154,0.06)]" : "rounded-md border-black/10"}`} data-testid="action-editor">
        <div className="flex items-center gap-2 mb-3 min-h-[1.75rem]">
          <Check size={13} className={`${navy ? "text-[#07539A]" : "text-[var(--copper)]"} shrink-0`} />
          <span className={`text-[11px] uppercase tracking-widest ${navy ? "text-[#07539A]" : "text-[var(--text-muted)]"}`}>{title}</span>
          {!single && items.filter((x) => x.trim()).length > 0 && (
            <span className="text-[10px] text-[var(--text-dim)] font-mono" data-testid="action-count">
              {items.filter((x) => x.trim()).length} action{items.filter((x) => x.trim()).length !== 1 ? "s" : ""}
            </span>
          )}
          <div className="ml-auto min-w-[4.5rem] flex justify-end shrink-0">
            {!readOnly && (dirty ? (
              <button
                onClick={save} disabled={saving} data-testid="action-save"
                className={`text-[11px] px-2 py-0.5 rounded text-[#FFFFFF] flex items-center gap-1 ${navy ? "bg-[#07539A] hover:bg-[#063F73]" : "bg-[var(--copper)] hover:bg-[var(--copper-strong)]"}`}
              >
                {saving ? <Loader2 size={11} className="animate-spin" /> : null} Save
              </button>
            ) : savedAt ? (
              <span className="text-[10px] text-[var(--success)] flex items-center gap-1"><Check size={11} /> Saved</span>
            ) : (
              <span className="text-[11px] invisible" aria-hidden="true">Save</span>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="flex items-start gap-2 group">
              <span className="text-[10px] font-mono text-[var(--text-dim)] mt-2.5 shrink-0">{single ? "→" : `${i + 1}.`}</span>
              {readOnly ? (
                <div className="flex-1 text-[13px] text-[var(--text)] py-1.5 leading-relaxed">{item || "—"}</div>
              ) : (
                <ActionField
                  value={item}
                  onChange={(e) => updateItem(i, e.target.value)}
                  onBlur={() => dirty && save()}
                  placeholder={single ? "What are you going to do?" : "Add an action…"}
                  testId={`action-input-${i}`}
                  navy={navy}
                />
              )}
              {!readOnly && !single && items.length > 1 && (
                <button onClick={() => removeItem(i)} data-testid={`action-remove-${i}`}
                        className="opacity-0 group-hover:opacity-100 text-[var(--text-dim)] hover:text-[var(--danger)] mt-2 shrink-0" aria-label="Remove">
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
          {!readOnly && !single && (
            <button onClick={addItem} data-testid="action-add"
                    className={`text-[11px] flex items-center gap-1 mt-1 ${navy ? "text-[#07539A] hover:text-[#063F73]" : "text-[var(--copper)] hover:text-[var(--copper-strong)]"}`}>
              <Plus size={11} /> Add action
            </button>
          )}
        </div>
      </div>

      {showInsight && !insightFirst && (
        <AIInsightPanel
          title={aiTitle}
          insight={insight}
          loading={regenerating || insightLoading}
          error={insightError}
          onRegenerate={!readOnly && onRegenerate ? regenerate : undefined}
          emptyText="No AI insight yet. Use refresh to generate one for this record."
          anchorId={insightAnchorId}
          alwaysShow
          markdown={insightMarkdown}
          accent={navy ? "navy" : "copper"}
          regenerateLabel={insightActionLabel}
        />
      )}
    </div>
  );
}
